import { useLayoutEffect, useRef, type ReactNode } from "react";
import { paletteCaptionLayout } from "./paletteCaptionLayout";
import { stripSoftHyphens } from "../../../src/utils/softHyphen";

export function usePaletteCaptionLayout(children: ReactNode, collapsed: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;
    const context = document.createElement("canvas").getContext("2d");
    if (!context) return;
    let active = true;
    const update = () => {
      if (!active) return;
      const labels = Array.from(container.querySelectorAll<HTMLElement>(".description"));
      const style = getComputedStyle(container);
      // Sharing spare caption space is specific to two columns. Wider palettes
      // wrap captions inside each grid cell instead of overlapping neighbouring icons.
      if (style.display !== "grid" || style.gridTemplateColumns.split(/\s+/).length !== 2) {
        for (const label of labels) { label.style.width = ""; label.style.transform = ""; label.style.hyphens = ""; }
        return;
      }
      const bounds = container.getBoundingClientRect();
      if (!bounds.width) return;
      for (let i = 0; i < labels.length; i += 2) {
        const row = labels.slice(i, i + 2);
        const sizes = row.map(label => {
          context.font = getComputedStyle(label).font;
          const text = label.textContent ?? "";
          const tool = label.closest(".tool")!.getBoundingClientRect();
          return {
            preferred: Math.ceil(context.measureText(stripSoftHyphens(text)).width) + 2,
            wholeWord: Math.ceil(Math.max(0, ...stripSoftHyphens(text).split(/\s+|(?<=[/-])/).map(word => context.measureText(word).width))) + 2,
            // Reserve room for an explicit syllable plus its visible hyphen.
            word: Math.ceil(Math.max(0, ...text.split(/\s+|(?<=[/-])/).flatMap(word => {
              const parts = word.split("\u00ad");
              return parts.map((part, index) => context.measureText(part + (index < parts.length - 1 ? "-" : "")).width);
            }))) + 2,
            center: tool.left - bounds.left + tool.width / 2,
          };
        });
        paletteCaptionLayout(bounds.width, sizes).forEach((layout, index) => {
          row[index].style.width = `${layout.width}px`;
          row[index].style.hyphens = layout.hyphenate ? "auto" : "none";
          row[index].style.transform = `translateX(${layout.left + layout.width / 2 - sizes[index].center}px)`;
        });
      }
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    window.addEventListener("resize", update);
    void document.fonts.ready.then(update);
    document.fonts.addEventListener("loadingdone", update);
    return () => {
      active = false;
      observer.disconnect();
      window.removeEventListener("resize", update);
      document.fonts.removeEventListener("loadingdone", update);
    };
  }, [children, collapsed]);
  return ref;
}
